/**
 * Keyboard map — pure key → action mapping for the workspace (doc 147 §5.3
 * redesign · "Linear-tier keyboard surface").
 *
 * The mapper is declarative and stateless: `mapKey(event, ctx)` returns an
 * abstract {@link KeyAction} that the view layer dispatches. The same table
 * (`KEY_BINDINGS`) feeds the `?` shortcuts overlay so the two can never
 * drift out of sync.
 *
 * Chord bindings (e.g. `g d`) are intentionally NOT supported here — adding
 * them would force the mapper to carry state. They live on the polish backlog.
 */

import { type WorkspaceMode } from './workspaceMachine.js';

export type KeyAction =
  | { kind: 'open-search' }
  | { kind: 'open-shortcuts' }
  | { kind: 'open-slicer' }
  | { kind: 'toggle-view-3d' }
  | { kind: 'focus-domain'; index: number }
  | { kind: 'open-entity' }
  | { kind: 'escape' }
  | { kind: 'walk-neighbor'; direction: 'up' | 'down' | 'left' | 'right' }
  | { kind: 'fit-focus' }
  | { kind: 'toggle-pin' }
  | { kind: 'copy-link' }
  | { kind: 'copy-typescript' }
  | { kind: 'copy-firestore-path' }
  | { kind: 'open-in-new-tab' }
  | { kind: 'open-entity-actions' };

export interface KeyContext {
  workspaceMode: WorkspaceMode;
  /** True when a text input / textarea / contenteditable owns focus. */
  inputActive: boolean;
  /** Set only by EntityDetailView's own listener; workspaceMode and this
   *  are mutually exclusive since focused-sheet no longer exists. */
  entityPage?: { entityId: string; selectedField?: string };
}

/** Minimal subset of KeyboardEvent the mapper reads — eases test mocking. */
export interface KeyboardEventLike {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

const isMod = (e: KeyboardEventLike): boolean => Boolean(e.metaKey || e.ctrlKey);

/**
 * Map a keyboard event to a {@link KeyAction}, or null if no binding fires.
 * Inputs suppress plain bindings (returning null) but modifier-bearing
 * shortcuts (⌘K, ⌘L, ⌘C, ⌘⏎) still apply.
 */
export function mapKey(event: KeyboardEventLike, ctx: KeyContext): KeyAction | null {
  // Modifier-bearing shortcuts — these survive input focus.
  if (isMod(event)) {
    const lower = event.key.toLowerCase();
    if (lower === 'k') return { kind: 'open-search' };
    if (lower === 'f' && event.shiftKey) return { kind: 'open-slicer' };
    // ⌘⌥3 (not ⌘⇧3) — ⌘⇧3 collides with the macOS system screenshot shortcut.
    if (lower === '3' && event.altKey) return { kind: 'toggle-view-3d' };
    if (lower === 'l' && hasFocus(ctx)) return { kind: 'copy-link' };
    if (lower === 'enter' && (ctx.workspaceMode.kind === 'focused' || ctx.entityPage !== undefined)) {
      return { kind: 'open-in-new-tab' };
    }
    if (lower === 'c' && ctx.entityPage !== undefined) {
      if (event.shiftKey) return { kind: 'copy-firestore-path' };
      if (ctx.entityPage.selectedField) return { kind: 'copy-typescript' };
    }
    return null;
  }

  // Plain keys are suppressed when an input is active.
  if (ctx.inputActive) return null;

  if (event.key === '/') return { kind: 'open-search' };
  if (event.key === '?') return { kind: 'open-shortcuts' };

  if (/^[1-9]$/.test(event.key)) {
    return { kind: 'focus-domain', index: Number(event.key) - 1 };
  }

  if (event.key === 'Escape' && hasFocus(ctx)) {
    return { kind: 'escape' };
  }

  if (event.key === 'Enter' && ctx.workspaceMode.kind === 'focused') {
    return { kind: 'open-entity' };
  }

  if (event.key.startsWith('Arrow') && hasFocus(ctx)) {
    const direction = arrowDirection(event.key);
    if (direction) return { kind: 'walk-neighbor', direction };
  }

  if (event.key === 'f' && hasFocus(ctx)) return { kind: 'fit-focus' };
  if (event.key === 'p' && hasFocus(ctx)) return { kind: 'toggle-pin' };
  // `e` opens the entity command palette while an entity is in focus.
  if (event.key === 'e' && hasFocus(ctx)) return { kind: 'open-entity-actions' };

  return null;
}

function hasFocus(ctx: KeyContext): boolean {
  return ctx.workspaceMode.kind !== 'idle';
}

function arrowDirection(key: string): 'up' | 'down' | 'left' | 'right' | null {
  if (key === 'ArrowUp') return 'up';
  if (key === 'ArrowDown') return 'down';
  if (key === 'ArrowLeft') return 'left';
  if (key === 'ArrowRight') return 'right';
  return null;
}

/* ── Catalogue for the ? overlay ────────────────────────────────────────── */

export interface KeyBinding {
  /** Display strings, e.g. ['⌘K', 'Ctrl+K']. */
  keys: string[];
  action: KeyAction['kind'];
  description: string;
  /** Which mode the binding is active in; for documentation only. */
  when: 'always' | 'idle-or-focused' | 'focused' | 'entity-page' | 'any-focus';
}

export const KEY_BINDINGS: readonly KeyBinding[] = [
  { keys: ['/'], action: 'open-search', description: 'Open search', when: 'always' },
  { keys: ['⌘K', 'Ctrl+K'], action: 'open-search', description: 'Open search', when: 'always' },
  { keys: ['?'], action: 'open-shortcuts', description: 'Open keyboard shortcuts', when: 'always' },
  { keys: ['⌘⇧F', 'Ctrl+⇧F'], action: 'open-slicer', description: 'Open persona slicer (compliance dress-rehearsal)', when: 'always' },
  { keys: ['⌘⌥3', 'Ctrl+Alt+3'], action: 'toggle-view-3d', description: 'Toggle 2D ↔ 3D constellation', when: 'always' },
  { keys: ['E'], action: 'open-entity-actions', description: 'Open the entity command palette', when: 'any-focus' },
  {
    keys: ['1', '2', '…', '9'],
    action: 'focus-domain',
    description: 'Jump to domain by index',
    when: 'idle-or-focused',
  },
  { keys: ['Enter'], action: 'open-entity', description: 'Open the entity page', when: 'focused' },
  { keys: ['Esc'], action: 'escape', description: 'Step back: focused → idle', when: 'any-focus' },
  { keys: ['↑↓←→'], action: 'walk-neighbor', description: 'Walk to a neighbor of the focused entity', when: 'any-focus' },
  { keys: ['f'], action: 'fit-focus', description: 'Fit focused entity + neighbors to viewport', when: 'any-focus' },
  { keys: ['p'], action: 'toggle-pin', description: 'Pin / unpin the focused entity', when: 'any-focus' },
  { keys: ['⌘L', 'Ctrl+L'], action: 'copy-link', description: 'Copy a deep link', when: 'any-focus' },
  { keys: ['⌘⏎', 'Ctrl+⏎'], action: 'open-in-new-tab', description: 'Open the entity page in a new tab', when: 'focused' },
  { keys: ['⌘C', 'Ctrl+C'], action: 'copy-typescript', description: 'Copy the selected field as a TypeScript declaration', when: 'entity-page' },
  { keys: ['⌘⇧C', 'Ctrl+⇧C'], action: 'copy-firestore-path', description: 'Copy the entity\'s Firestore path', when: 'entity-page' },
];
